import { genderList } from "constants/general";
import { Nullable } from "types/global";

export type Anniversary = {
  anniversaryType: "birthday" | "registration";
  days: number;
  type: "anniversary-birthday" | "anniversary-registration";
};
export type MemberRegistrationDate = {
  operator: { code: "" };
  days: number;
  type: "member_registration_date";
};
export type LastPurchaseWasNDaysAgo = {
  days: number;
  type: "last_purchase_n_days_before";
};
export type LastPurchaseWithin = {
  fromDays: number;
  toDays: number;
  type: "last_transaction_between_days";
};
export type LastCustomEventWasNDaysAgo = {
  days: number;
  type: "last_custom_event_n_days_before";
  customEventType?: string;
};
export type LastCustomEventWithin = {
  fromDays: number;
  toDays: number;
  type: "last_custom_event_between_days";
  customEventType?: string;
};
export type AverageTransactionAmount = {
  fromAmount: number;
  toAmount: number;
  type: "average_transaction_amount";
};
export type TransactionValue = {
  fromAmount: number;
  toAmount: number;
  type: "transaction_amount";
};
export type BoughtProductsWithLabels = {
  labels: { key: string; value: string }[];
  type: "bought_labels";
};
export type MembersHaveSuchLabelsValues = {
  labels: { key: string; value: string }[];
  type: "customer_with_labels_values";
};
export type MembersWhoHaveSuchLabels = {
  labels: { key: string }[];
  type: "customer_has_labels";
};
export type BoughtInSpecificChannel = {
  channelIds: string[];
  type: "bought_through_channel";
};
export type BoughtSpecificBrands = {
  makers: string[];
  type: "bought_makers";
};
export type BoughtSpecificSKU = {
  skuIds: string[];
  type: "bought_skus";
};
export type CustomMemberList = {
  customers: string[];
  type: "customer_list";
};
export type PurchasePeriod = {
  fromDate: Nullable<string>;
  toDate: Nullable<string>;
  type: "purchase_period";
};
export type TransactionCount = {
  max: number;
  min: number;
  type: "transaction_count";
};
export type TransactionPercentInChannel = {
  percent: number;
  channelId: string;
  type: "transaction_percent_in_channel";
};
type GenderOptions = (typeof genderList)[number];
export type Gender = {
  options: GenderOptions[];
  type: "gender";
};
export type DailyGrossValueInGivenPeriod = {
  fromAmount: number;
  fromDate: Nullable<string>;
  toAmount: number;
  toDate: Nullable<string>;
  type: "transaction_amount_each_day_in_period";
};
export type DailyGrossValueInLastNDays = {
  days: number;
  fromAmount: number;
  toAmount: number;
  type: "transaction_amount_each_day_in_last_n_days";
};
export type TotalGrossValueInGivenPeriod = {
  fromAmount: number;
  fromDate: Nullable<string>;
  toAmount: number;
  toDate: Nullable<string>;
  type: "transaction_amount_in_period";
};
export type TotalGrossValueInLastNDays = {
  days: number;
  fromAmount: number;
  toAmount: number;
  type: "transaction_amount_in_last_n_days";
};
export type Country = {
  countries: string[];
  type: "country";
};
export type CityType = {
  cities: string[];
  type: "city";
};
export type ProvinceType = {
  provinces: string[];
  type: "province";
};
export type PostalCodeType = {
  postal: string[];
  type: "postal";
};
export type AgeType = {
  fromAge: number;
  toAge: number;
  type: "age";
};
export type MemberTiersType = {
  tiers: string[];
  type: "customer_in_tiers";
};
export type CustomerCsvList = {
  type: "customer_csv_list";
};
export type AchievementCompletionType = {
  achievementId: string;
  operator: { code: string };
  completionCount?: number;
  fromCompletionCount?: number;
  toCompletionCount?: number;
  type: "achievement_completion_count";
};

export enum Timeframes {
  LAST_X_DAYS = "last_x_days",
  LAST_X_WEEKS = "last_x_weeks",
  LAST_X_MONTHS = "last_x_months",
}

export const campaignCompletionTimeframes = [
  { label: "lastXDays", value: Timeframes.LAST_X_DAYS },
  { label: "lastXWeeks", value: Timeframes.LAST_X_WEEKS },
  { label: "lastXMonths", value: Timeframes.LAST_X_MONTHS },
] as const;

export type CampaignCompletionTimeframe = `${Timeframes}`;

export type CampaignCompletionType = {
  campaignId: string;
  timeframe: {
    type: CampaignCompletionTimeframe;
    number: number;
  };
  completionNumber: number;
  type: "campaign_completion";
};
export type AchievementProgressionType = {
  achievementId: string;
  achievementRuleId: string;
  operator: { code: string };
  progress?: number;
  fromProgress?: number;
  toProgress?: number;
  type: "achievement_rule_progress";
};
export type NumberOfTransactionsInGivenPeriod = {
  operator: { code: string };
  fromDate: string | null;
  toDate: string | null;
  count?: number;
  fromCount?: number;
  toCount?: number;
  type: "transaction_count_in_period";
};

export type Condition =
  | Anniversary
  | AverageTransactionAmount
  | BoughtInSpecificChannel
  | BoughtProductsWithLabels
  | BoughtSpecificBrands
  | BoughtSpecificSKU
  | Country
  | CustomerCsvList
  | CustomMemberList
  | DailyGrossValueInGivenPeriod
  | DailyGrossValueInLastNDays
  | Gender
  | LastCustomEventWasNDaysAgo
  | LastCustomEventWithin
  | LastPurchaseWasNDaysAgo
  | LastPurchaseWithin
  | MembersHaveSuchLabelsValues
  | MembersWhoHaveSuchLabels
  | PurchasePeriod
  | TotalGrossValueInGivenPeriod
  | TotalGrossValueInLastNDays
  | TransactionCount
  | TransactionPercentInChannel
  | TransactionValue;

export type SegmentFECondition = {
  component?: React.ComponentType<{
    formField: string;
    index: number;
    status: any;
    values: any;
    setFieldValue: (field: string, value: any) => void;
  }>;
  initialValues?: any;
  label?: string;
  category?: string;
  defaultValues?: any;
  isConditionSaved?: boolean;
  type?: string;
};
